import { BMHScreenProps } from "./types";
import { Button, Container, Spacer } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import styled, { css } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Platform, View } from "react-native";
import { useCanRequestAgain, useStockItemRequest } from "@apis/BMH";
import { messageType } from "../PopUp/types";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import Loader from "@components/atoms/Loader";
import BmhForm from "./BmhForm";
import { ProductCategory } from "@utils/constants";
import { getButtonCategory } from "../Share/utils/utils";
import { CanRequestData } from "@src/types/CanRequestAgain";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import {
  EventName,
  EventType,
  getAnalyticsProductData,
} from "@utils/analytics";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections, SubSection } from "@utils/customization";
import { useQueryClient } from "@tanstack/react-query";
import { RequestTypes } from "@scenes/requests/utils/utils";
import { PriceModes } from "../PriceModeCard/types";
import {
  KeyboardAwareScrollView,
  KeyboardStickyView,
} from "react-native-keyboard-controller";
import { mapDataToLabels } from "../DynamicForm/utils";
import { useSelector } from "react-redux";
import CustomTabs from "./components/CustomTabs";
import ConfirmationPopup from "@components/atoms/ConfirmationPopUp";
import { useEffect, useState } from "react";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { processAndFormatSubSections } from "./utils";
import { useDynamicFormState } from "@scenes/bulk-request/context/DynamicFormContext";
import { useUpdateReadRequestStatus } from "@apis/requestHistory";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { MemoStatus } from "@src/types/HistoryRequest";
import WarningMessage from "@components/atoms/WarningMessage";
import { WarningType } from "@components/atoms/WarningMessage/types";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { isPair, isParcel } from "@utils/product/productValidators";
import {
  formatNumberWithPrecision,
  removePercentage,
} from "@utils/product/productFormatters";
import { DefaultTheme } from "styled-components/native";
import { useAllowQuantityOnBulk } from "@redux/slices/globalSlice";
import QuantitySelection from "./QuantitySelection";

const BMHScreen = ({
  title,
  itemDetail,
  onClose,
  productType,
  externalPriceMode,
  initialSelectedOption,
  initialSelectedQuantity = 1,
  onSelectedQuantityChange,
  additionalAttributes,
  fromPDPPage,
}: BMHScreenProps) => {
  const { theme, t } = useScreenOptionsHooks();
  const userInfo = useUserInfo();
  const priceDisclaimerLayout = useGetPdpSectionLayout(
    PdpSections.PRICE_DISCLAIMER,
    productType
  );
  const customForm = useSelector(
    (state: any) => state.globalData?.app_settings?.custom_form
  );
  const disabledMemo = itemDetail?.vendor_accepts_memo !== true;
  const allowQuantityOnBulk = useAllowQuantityOnBulk();
  const Hours = userInfo.user.feature_settings.max_hold_hours;
  const PARequest = userInfo.isPAUser();
  const firstHoursNumber = Number(Hours[0]);
  const firstLabel =
    firstHoursNumber > 72
      ? `${firstHoursNumber / 24} ${t("bmh.hold.hour.type.days.text")}`
      : `${firstHoursNumber} ${t("bmh.hold.hour.type.hour.text")}`;
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showRequestPopup, setShowRequestPopup] = useState(false);
  const [disableRequest, setDisableRequest] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    itemDetail?.company_identifier ? "" : disabledMemo ? "buy" : "memo"
  );
  const [requestId, setRequestId] = useState<any>("");
  const [selectedQuantity, setSelectedQuantity] = useState<number>(
    initialSelectedQuantity
  );
  const [requestedStockItem, setRequestedStockItem] = useState({});
  const [isPurchasingAgent, setIsPurchasingAgent] = useState(false);
  const [subSections, setSubSections] = useState<SubSection[]>([]);
  const priceMode = userInfo?.user?.price_mode;
  let mPriceMode = externalPriceMode ? externalPriceMode : priceMode;
  const queryClient = useQueryClient();
  const { trackButtonEvent } = useUserAnalytics() as any;
  const requestNameRequestPlaced = isPurchasingAgent && RequestTypes.Internal;
  const [responseData, setResponseData] = useState<CanRequestData>();
  const updateRead = useUpdateReadRequestStatus(requestId);
  const ScrollContainer =
    Platform.OS === "web" ? View : KeyboardAwareScrollView;
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);

  const stoneType = isPair(itemDetail)
    ? SearchFilterTypeEnum.PAIRS
    : isCustomApp && isParcel(itemDetail)
    ? SearchFilterTypeEnum.PARCEL
    : SearchFilterTypeEnum.SINGLE_STONES;

  const {
    dynamicFormValues: customFormData,
    isDynamicFormValid: customFormValidation,
  } = useDynamicFormState();
  const isLooseParcel =
    itemDetail?.diamond_category === SearchFilterTypeEnumLowercase.LOOSE_PARCEL;
  const requestedQuantity =
    customFormData?.singleForm?.[itemDetail.id]?.requestedQuantity;

  const parcelFieldNames = ["requestedTotalCarat", "requestedQuantity"];
  const singleFormValues = customFormData?.singleForm?.[itemDetail.id] ?? {};
  const singleFormErrors =
    (customFormValidation as any)?.singleForm?.errors ?? {};
  const isSingleFormValidIgnoringParcelErrors = isLooseParcel
    ? (customFormValidation as any)?.singleForm?.isValid ||
      Object.keys(singleFormErrors).every((k) => {
        if (!parcelFieldNames.includes(k)) return false;
        const val = singleFormValues[k];
        return val !== undefined && val !== null && val !== "";
      })
    : (customFormValidation as any)?.singleForm?.isValid;
  const requestFormValues = customFormData?.requestForm ?? {};
  const requestFormErrors =
    (customFormValidation as any)?.requestForm?.errors ?? {};
  const isRequestFormValidIgnoringParcelErrors = isLooseParcel
    ? (customFormValidation as any)?.requestForm?.isValid ||
      Object.keys(requestFormErrors).every((k) => {
        if (!parcelFieldNames.includes(k)) return false;
        const val = requestFormValues[k];
        return val !== undefined && val !== null && val !== "";
      })
    : (customFormValidation as any)?.requestForm?.isValid;

  const requestAgainId = responseData?.stock_item_request?.id;
  const requestName =
    responseData?.stock_item_request?.purchase_agent === true &&
    RequestTypes.Internal;
  const noRequest = responseData?.can_request_again === false;
  const disableSoldReturnRequest =
    responseData?.stock_item_request?.additional_attributes?.memo_status &&
    (responseData?.stock_item_request?.additional_attributes?.memo_status ===
    MemoStatus.Sold
      ? MemoStatus.Return
      : MemoStatus.Sold);

  const stockItemRequest = useStockItemRequest();
  const defaultValues = {
    comments: "",
    memoComments: "",
    buyComments: "",
    holdComments: "",
    ctOffer: 0,
    discountPercent: 0,
    priceOffer: 0,
    selectedRadio: "COD",
    payWithIn: 30,
    selectedHoldHours: firstLabel,
    hold_hours: firstLabel,
    read_by_buyer: true,
    quantity: selectedQuantity,
  };

  const Value = (val: string) => {
    return val.replace(/,/g, "");
  };
  const [dataBuy, setDataBuy] = useState(defaultValues);
  const [dataMemo, setDataMemo] = useState(defaultValues);
  const [dataHold, setDataHold] = useState(defaultValues);
  const sectionLayout = useGetPdpSectionLayout(
    customForm ? PdpSections.CUSTOM_BMH : PdpSections.BUY_MEMO_HOLD_SECTION,
    productType,
    stoneType
  );
  const canRequestAgain = useCanRequestAgain(
    itemDetail?.id || 0,
    selectedOption || ""
  );

  const formInputs = processAndFormatSubSections(
    sectionLayout,
    selectedOption,
    priceMode
  );

  const store_customer_attributes = {
    ...mapDataToLabels(
      selectedOption === EventType.BUY
        ? customFormData?.singleForm?.[itemDetail?.id]
        : customFormData.requestForm,
      formInputs
    ),
    ...(customFormData?.requestForm?.account_id && {
      "Account Id": customFormData.requestForm.account_id,
    }),
  };
  const handleRequestChange = (option: string) => {
    setSelectedOption(option);
    title(t(`bmh.title.${option}.text`));
  };
  const closePopup = () => {
    queryClient.refetchQueries({ queryKey: ["stock_item_request"] }).then();
    setShowRequestPopup(false);
  };

  const openPopup = () => {
    setShowRequestPopup(true);
  };

  const callUpdateUnread = () => {
    const data = { read_by_buyer: false };
    updateRead.mutate(data, {
      onSuccess() {
        queryClient
          .invalidateQueries({ queryKey: ["GET_UNREAD_REQUEST"] })
          .then();
      },
    });
  };

  const closeSuccessPopup = () => {
    callUpdateUnread();
    setShowSuccessPopup(false);
    onClose();
  };
  const defaultSubSections: SubSection[] = [
    { name: "bmh_memo", label: "bmh.topTabs.memo.text", order: 1 },
    { name: "bmh_buy", label: "bmh.topTabs.buy.text", order: 2 },
    { name: "bmh_hold", label: "bmh.topTabs.hold.text", order: 3 },
  ];

  useEffect(() => {
    setSelectedQuantity(initialSelectedQuantity);
  }, [initialSelectedQuantity]);

  const handleSelectedQuantityChange = (quantity: number) => {
    setSelectedQuantity(quantity);
    onSelectedQuantityChange?.(quantity);
  };

  useEffect(() => {
    if (sectionLayout?.["sub-sections"]) {
      const subSectionsObj = Object.values(sectionLayout?.["sub-sections"]);
      let sortedSubSections = [...subSectionsObj].sort(
        (a, b) => a.order - b.order
      );
      const filterNames = [
        RequestTypes.Buy.toLowerCase(),
        RequestTypes.Memo.toLowerCase(),
        RequestTypes.Hold.toLowerCase(),
      ];
      if (
        userInfo.companyIdentifierEnabled() &&
        itemDetail?.company_identifier
      ) {
        sortedSubSections = sortedSubSections.filter(
          (item) => !filterNames?.includes(item?.name?.split("_")[1])
        );
      } else {
        sortedSubSections = sortedSubSections.filter(
          (item) => ![MemoStatus.Sold, MemoStatus.Return]?.includes(item?.name)
        );
      }
      if (
        userInfo.isMemoInOrangeModeOrPAUser(priceMode) &&
        externalPriceMode === PriceModes.ORANGE
      ) {
        setSubSections([]);
      } else {
        setSubSections(sortedSubSections);
      }
      if (sortedSubSections.length > 0) {
        const initialOption =
          initialSelectedOption || sortedSubSections[0].name.split("_")[1];
        if (
          userInfo.companyIdentifierEnabled() &&
          itemDetail?.company_identifier
        ) {
          setSelectedOption(sortedSubSections[0].name);
          title(t(`bmh.title.${sortedSubSections[0].name}.text`));
        } else if (disabledMemo && initialOption === "memo") {
          const nextItem = sortedSubSections.find(
            (item) => item.name.split("_")[1] !== "memo"
          );
          if (nextItem) {
            const nextOption = nextItem.name.split("_")[1];
            setSelectedOption(nextOption);
            title(t(`bmh.title.${nextOption}.text`));
          }
        } else {
          setSelectedOption(initialOption);
          title(t(`bmh.title.${initialOption}.text`));
        }
      }
    } else {
      setSubSections(defaultSubSections);
    }
    if (disabledMemo) {
      title(t("bmh.title.buy.text"));
    }
  }, [sectionLayout?.["sub-sections"], disabledMemo]);

  useEffect(() => {
    const data: any = canRequestAgain.data?.data.data;

    if (!disableSoldReturnRequest) {
      setResponseData(data);
    }
  }, [
    canRequestAgain.isSuccess,
    canRequestAgain.data?.data?.data?.can_request_again,
  ]);

  useEffect(() => {
    if (
      responseData?.stock_item_request?.additional_attributes?.memo_status ===
      MemoStatus.Return
    ) {
      setSelectedOption(MemoStatus.Return);
      title(t(`bmh.title.${MemoStatus.Return}.text`));
    }
  }, [selectedOption, disableSoldReturnRequest]);
  const navigateToRequest = (requestType?: any) => {
    requestType === "new" && setShowSuccessPopup(false);
    onClose();
    const navigateParams = {
      selectedTab: "sent",
      initial: false,
      requestedItem:
        requestType === "new"
          ? requestedStockItem
          : responseData?.stock_item_request,
      screen: Routes.RequestDetailScreen,
      params: {
        id: requestType === "new" ? requestId : requestAgainId,
        selectedTab: "sent",
        requestName:
          requestType === "new" ? requestNameRequestPlaced : requestName,
        productType: productType ?? ProductCategory.DEFAULT,
        externalPriceMode: mPriceMode,
        price_mode: mPriceMode,
      },
    };
    navigate(Routes.AppNavigator, {
      screen: Routes.RequestHistoryNavigator,
      params: navigateParams,
    });
  };

  const handleEvent = () => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      selectedOption === "memo"
        ? EventType.MEMO
        : selectedOption === "buy"
        ? EventType.BUY
        : EventType.HOLD,
      getAnalyticsProductData(itemDetail)
    );
    stockItemRequest.mutate(
      {
        comments: customForm
          ? selectedOption === "buy"
            ? customFormData.singleForm[itemDetail?.id].comments
            : customFormData.requestForm.comments
          : selectedOption === "memo" ||
            selectedOption === MemoStatus.Sold ||
            selectedOption === MemoStatus.Return
          ? dataMemo.memoComments
          : selectedOption === "buy"
          ? dataBuy.buyComments
          : dataHold.holdComments,
        ...(PARequest && {
          location_id: itemDetail?.locations[0].id,
        }),
        price_mode: mPriceMode,
        request_type:
          selectedOption === "memo" ||
          selectedOption === MemoStatus.Sold ||
          selectedOption === MemoStatus.Return
            ? `${itemDetail?.type}_memo`
            : selectedOption === "buy"
            ? `buy_${itemDetail?.type}`
            : `hold_${itemDetail?.type}`,
        stock_item_id: itemDetail?.id,
        read_by_buyer: true,
        ...(PARequest &&
          selectedOption === "memo" && {
            offer_total_sales_price: itemDetail?.total_sales_price,
          }),
        ...(customForm && { store_customer_attributes }),
        ...(itemDetail?.type !== ProductCategory.JEWELRY &&
          selectedOption === "buy" && {
            offer_discount_percent: customForm
              ? removePercentage(
                  customFormData.singleForm[itemDetail?.id]?.totalOfferDiscount
                ) || itemDetail?.discount_percent
              : `${dataBuy.discountPercent}`,
            offer_price_per_carat: customForm
              ? customFormData.singleForm[itemDetail?.id]?.pricePerOffer
                ? customFormData.singleForm[
                    itemDetail?.id
                  ]?.pricePerOffer.replace(/,/g, "")
                : itemDetail?.price_per_carat
              : Value(String(dataBuy.ctOffer)),
          }),
        ...(selectedOption === "buy" && {
          offer_total_sales_price: customForm
            ? customFormData.singleForm[itemDetail?.id]?.totalOfferPrice
              ? customFormData.singleForm[
                  itemDetail?.id
                ]?.totalOfferPrice.replace(/,/g, "")
              : itemDetail?.total_sales_price
            : Value(String(dataBuy.priceOffer)),
          ...(customFormData.singleForm[itemDetail?.id]?.requestedTotalCarat &&
            Number(
              customFormData.singleForm[
                itemDetail?.id
              ]?.requestedTotalCarat.replace(" ct", "")
            ) > 0 && {
              offer_size: customFormData.singleForm[
                itemDetail?.id
              ]?.requestedTotalCarat.replace(" ct", ""),
            }),
          ...(customFormData.singleForm[itemDetail?.id]?.requestedQuantity &&
            Number(
              customFormData.singleForm[itemDetail?.id]?.requestedQuantity
            ) > 0 && {
              quantity:
                customFormData.singleForm[itemDetail?.id]?.requestedQuantity,
              offer_total_sales_price: formatNumberWithPrecision(
                Number(
                  Value(
                    String(
                      customFormData.singleForm[
                        itemDetail?.id
                      ]?.requestedTotalCarat.replace(" ct", "")
                    )
                  )
                ) * Number(Value(String(dataBuy.ctOffer))),
                { precision: 2 }
              ),
            }),
        }),
        ...(selectedOption === "buy" && customForm
          ? customFormData.singleForm[itemDetail?.id]?.radioSelect === "invoice"
            ? {
                payment_type: "Invoice",
                pay_within:
                  customFormData.singleForm[itemDetail?.id]?.payWithin,
              }
            : customFormData.singleForm[itemDetail?.id]?.radioSelect === "cod"
            ? { payment_type: "COD" }
            : { payment_type: "Invoice" }
          : selectedOption === "buy" &&
            (dataBuy.selectedRadio === "COD"
              ? { payment_type: "COD" }
              : { payment_type: "Invoice", pay_within: dataBuy.payWithIn })),
        ...(selectedOption === "hold" && {
          hold_hours: customForm
            ? customFormData.requestForm.hold_hours
            : dataHold.hold_hours,
        }),
        ...(itemDetail?.display_configuration_v2 &&
          itemDetail?.custom_design_configuration && {
            additional_attributes: additionalAttributes,
          }),
        ...(userInfo.enableSellReturnButton() &&
          itemDetail?.company_identifier && {
            memo_status:
              selectedOption === MemoStatus.Sold
                ? MemoStatus.Sold
                : MemoStatus.Return,
          }),
        ...(allowQuantityOnBulk &&
          productType === ProductCategory.JEWELRY && {
            quantity: selectedQuantity,
          }),
      },
      {
        onSuccess(data: any) {
          setRequestId(data.data.data.stock_item_request.id);
          setRequestedStockItem(data.data.data.stock_item_request);
          setIsPurchasingAgent(
            data.data.data.stock_item_request?.purchase_agent
          );
          closePopup();
          if (
            itemDetail?.company_identifier &&
            userInfo.companyIdentifierEnabled()
          ) {
            setShowSuccessPopup(false);
            onClose();
            const navigateParams = {
              selectedTab: "sent",
              initial: false,
              requestedItem: data.data.data.stock_item_request,
              screen: Routes.RequestDetailScreen,
              params: {
                id: data.data.data.stock_item_request.id,
                selectedTab: "sent",
                requestName: requestNameRequestPlaced,
                productType: productType ?? ProductCategory.DEFAULT,
                externalPriceMode: mPriceMode,
                price_mode: mPriceMode,
              },
            };
            setTimeout(() => {
              navigate(Routes.RequestHistoryNavigator, navigateParams);
            }, 1000);
          } else {
            setShowSuccessPopup(true);
          }
        },
      }
    );
  };

  const keyboardOffset = {
    closed: 0,
    opened: theme.isMobile && Platform.OS === "ios" ? 35 : 0,
  };

  return (
    <Overlay>
      <ScrollContainer bottomOffset={120} keyboardShouldPersistTaps="handled">
        {!PARequest && subSections.length > 1 && (
          <>
            {!theme.isLargeScreen ? (
              <Spacer size={SpaceType.LargeDouble} />
            ) : null}
            <HeaderButtonContainer>
              <CustomTabs
                testIDPrefix="bmh.screen.tabs"
                subSections={subSections}
                handleRequestChange={handleRequestChange}
                selectedOption={selectedOption}
                disabledMemo={disabledMemo}
                itemDetail={itemDetail}
                price_mode={priceMode}
                disableSoldReturnRequest={disableSoldReturnRequest}
              />
            </HeaderButtonContainer>
            <BorderLine />
          </>
        )}
        <Spacer size={SpaceType.Big} />
        {canRequestAgain.isLoading ? (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        ) : (
          canRequestAgain.isSuccess && (
            <Container maxWidth={686}>
              <BmhForm
                selectedOption={selectedOption}
                canRequestAgain={responseData}
                itemDetail={itemDetail}
                onDataBuyChange={setDataBuy}
                onDataMemoChange={setDataMemo}
                onDataHoldChange={setDataHold}
                disableRequestButton={setDisableRequest}
                customForm={customForm}
                customFormInputs={formInputs}
                dataHold={dataHold}
              />
              {!customForm && (
                <Spacer
                  size={
                    priceDisclaimerLayout?.price_modes?.includes(priceMode)
                      ? SpaceType.Big
                      : SpaceType.large
                  }
                />
              )}
            </Container>
          )
        )}
        {fromPDPPage &&
          priceDisclaimerLayout?.price_modes?.includes(priceMode) &&
          priceDisclaimerLayout?.description && (
            <>
              {customForm && <Spacer size={SpaceType.Big} />}
              <Container maxWidth={686}>
                <WarningMessage
                  type={WarningType.WARNING}
                  message={t(priceDisclaimerLayout?.description as string)}
                  typographyStyle={"Minor-R"}
                  textColor={theme["core-05"]}
                />
              </Container>
              {customForm && <Spacer size={SpaceType.Big} />}
            </>
          )}
      </ScrollContainer>
      {theme.isLargeScreen ? <Spacer size={SpaceType.large} /> : null}

      {!theme.isLargeScreen ? <BorderLine /> : null}

      {allowQuantityOnBulk && productType === ProductCategory.JEWELRY && (
        <>
          {!theme.isLargeScreen && <Spacer size={SpaceType.Big} />}
          <QuantitySelection
            itemDetail={itemDetail}
            selectedQuantity={selectedQuantity}
            setSelectedQuantity={handleSelectedQuantityChange}
          />
        </>
      )}

      <KeyboardStickyView offset={keyboardOffset}>
        <ButtonContainer itemType={itemDetail?.type} theme={theme}>
          <ContainerOverlay>
            <Button
              type={ButtonType.primary}
              category={getButtonCategory(itemDetail?.type)}
              testID={
                noRequest
                  ? responseData?.stock_item_request
                    ? "bmh.screen.button.checkOffer"
                    : "bmh.screen.button.okay"
                  : selectedOption === "buy"
                  ? "bmh.screen.button.buy"
                  : "bmh.screen.button.placeRequest"
              }
              title={
                noRequest
                  ? responseData?.stock_item_request
                    ? t("bmh.button.title.checkOffer.text")
                    : t("bmh.button.title.okay.text", "Okay")
                  : selectedOption === "buy"
                  ? t("bmh.button.title.buy.text")
                  : t("bmh.button.title.placeRequest.text")
              }
              onPress={
                noRequest
                  ? responseData?.stock_item_request
                    ? () => navigateToRequest("check")
                    : () => onClose()
                  : openPopup
              }
              disabled={
                customForm && !noRequest
                  ? selectedOption === "buy"
                    ? !isSingleFormValidIgnoringParcelErrors ||
                      (isLooseParcel
                        ? requestedQuantity === undefined ||
                          requestedQuantity === ""
                        : false)
                    : !isRequestFormValidIgnoringParcelErrors
                  : disableRequest
              }
            />
          </ContainerOverlay>
        </ButtonContainer>
      </KeyboardStickyView>
      <ConfirmationPopup
        opened={showRequestPopup}
        title={
          selectedOption === "memo"
            ? userInfo.isPAUser()
              ? t("bmh.title.request.text")
              : t("bmh.title.memo.text")
            : selectedOption === "buy"
            ? t("bmh.title.buy.text")
            : selectedOption === MemoStatus.Sold
            ? t("bmh.title.sold.text")
            : selectedOption === MemoStatus.Return
            ? t("bmh.title.return.text")
            : t("bmh.title.hold.text")
        }
        htmlMessage={t("bmh.popUp.discription.text")}
        primaryButtonTitle={t("bmh.popUp.button.yes.text")}
        secondaryButtonTitle={t("bmh.popUp.button.dismiss.text")}
        primaryButtonAction={handleEvent}
        secondaryButtonAction={() => {
          closePopup();
        }}
        primaryButtonLoading={stockItemRequest.isLoading}
        category={getButtonCategory(itemDetail?.type)}
        messageType={messageType.Generic}
        primaryButtonType={ButtonType.primary}
        secondaryButtonType={ButtonType.secondary}
        styleKey={"Body-R"}
      />
      <ConfirmationPopup
        opened={showSuccessPopup}
        title={t("bmh.successPopUp.title.text")}
        htmlMessage={t("bmh.successPopUp.discription.text")}
        primaryButtonTitle={t("bmh.successPopUp.button.product.text")}
        secondaryButtonTitle={t("bmh.successPopUp.button.request.text")}
        primaryButtonAction={closeSuccessPopup}
        secondaryButtonAction={() => {
          navigateToRequest("new");
        }}
        category={getButtonCategory(itemDetail?.type)}
        messageType={messageType.Success}
        primaryButtonType={ButtonType.primary}
        secondaryButtonType={ButtonType.secondary}
        styleKey={"Body-R"}
      />
    </Overlay>
  );
};

const Overlay = styled.View`
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen &&
    css`
      flex: 1;
      min-height: 580px;
    `}
`;

const HeaderButtonContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  ${({ theme }: { theme: DefaultTheme }) =>
    Platform.OS !== "web" &&
    !theme.isMobile &&
    css`
      max-width: 750px;
      margin: 0 auto;
    `}
`;

const BorderLine = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const LoaderContainer = styled.View`
  padding: ${SpaceType.ExtraLarge}px;
`;

const ButtonContainer = styled.View<{
  itemType?: any;
  theme?: any;
}>`
  flex-shrink: 1;
  padding-top: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? `${SpaceType.Double}px` : `${SpaceType.Big}px`};
  padding-bottom: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen
      ? `${SpaceType.Double}px`
      : `${SpaceType.ExtraLarge}px`};
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  padding-horizontal: ${SpaceType.Double}px;
  align-items: center;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen &&
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen &&
    !theme.isMobile &&
    css`
      max-width: 343px;
    `}
`;

export default BMHScreen;
